import React from 'react';
import { Clock, Play, Pause } from 'lucide-react';
import { Card, Button, EmptyState } from '../ui';
import './SessionLog.css';

export interface WorkSession {
  id: string;
  projectId: string;
  startTime: string;
  endTime?: string;
  duration: number; // in minutes
  notes?: string;
  stage: string;
}

interface SessionLogProps {
  projectId: string;
  sessions?: WorkSession[];
  onStartSession?: () => void;
  onEndSession?: (sessionId: string) => void;
  isLoading?: boolean;
}

const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const SessionLog: React.FC<SessionLogProps> = ({
  projectId,
  sessions = [],
  onStartSession,
  onEndSession,
  isLoading = false,
}) => {
  const activeSession = sessions.find(s => !s.endTime);
  const completedSessions = sessions.filter(s => s.endTime);
  const totalTime = sessions.reduce((acc, s) => acc + s.duration, 0);

  if (isLoading) {
    return (
      <Card className="session-log session-log--loading">
        <div className="session-log__spinner" />
        <p>Loading sessions...</p>
      </Card>
    );
  }

  return (
    <Card className="session-log">
      <div className="session-log__header">
        <h3 className="session-log__title">
          <Clock size={18} />
          Work Sessions
        </h3>
        <span className="session-log__total">
          Total: {formatDuration(totalTime)}
        </span>
      </div>

      {/* Active Session */}
      {activeSession && (
        <div className="session-log__active">
          <div className="session-log__active-indicator">
            <span className="session-log__pulse" />
            <span>Session in progress</span>
          </div>
          <div className="session-log__active-info">
            <span className="session-log__stage">{activeSession.stage}</span>
            <span className="session-log__start">
              Started {formatDateTime(activeSession.startTime)}
            </span>
          </div>
          {onEndSession && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onEndSession(activeSession.id)}
            >
              <Pause size={14} />
              End Session
            </Button>
          )}
        </div>
      )}

      {/* Start Session Button */}
      {!activeSession && onStartSession && (
        <Button
          variant="primary"
          className="session-log__start-btn"
          onClick={onStartSession}
        >
          <Play size={16} />
          Start Work Session
        </Button>
      )}

      {/* Session History */}
      {completedSessions.length > 0 && (
        <div className="session-log__list">
          {completedSessions.map(session => (
            <div key={session.id} className="session-log__item">
              <div className="session-log__item-main">
                <span className="session-log__item-stage">{session.stage}</span>
                <span className="session-log__item-duration">
                  {formatDuration(session.duration)}
                </span>
              </div>
              <div className="session-log__item-time">
                {formatDateTime(session.startTime)}
                {session.endTime && ` - ${formatDateTime(session.endTime)}`}
              </div>
              {session.notes && (
                <p className="session-log__item-notes">{session.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state for no sessions */}
      {sessions.length === 0 && !activeSession && (
        <EmptyState
          variant="no-sessions"
          title="No work sessions yet"
          message="Start a session when you're ready to work. Each session is a step forward in your creative journey."
        />
      )}
    </Card>
  );
};

export default SessionLog;
